import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, {forwardRef, PropsWithChildren, Ref} from 'react';
import {TouchTarget} from './button';
import {Link} from './link';

interface Props extends React.HTMLAttributes<HTMLSpanElement> {
  src?: string;
  square?: boolean;
  initials?: string;
  alt?: string;
  className?: any;
}

export function Avatar({
  src,
  square = false,
  initials,
  alt = '',
  children,
  className,
  ...props
}: PropsWithChildren<Props>) {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={clsx(
        className,
        // Basic layout
        'inline-grid shrink-0 align-middle [--avatar-radius:20%] [--ring-opacity:20%] *:col-start-1 *:row-start-1',
        'outline outline-1 -outline-offset-1 outline-black/[--ring-opacity] dark:outline-white/[--ring-opacity]',
        // Add the correct border radius
        square
          ? 'rounded-[--avatar-radius] *:rounded-[--avatar-radius]'
          : 'rounded-full *:rounded-full',
      )}>
      {initials && (
        <svg
          className="size-full select-none fill-current p-[5%] text-[48px] font-medium uppercase"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : 'true'}>
          {alt && <title>{alt}</title>}
          <text
            x="50%"
            y="50%"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            textAnchor="middle"
            dy=".125em">
            {initials}
          </text>
        </svg>
      )}
      {children && children}
      {src && <img className="size-full" src={src} alt={alt} />}
    </span>
  );
}

type CommonProps = {
  src?: string;
  square?: boolean;
  initials?: string;
  alt: string;
  className: any;
};

// Props when href is present → it's a link
type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

// Props when no href → it's a button
type CatalystButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: undefined;
} & Headless.ButtonProps;

export type AvatarButtonProps = CommonProps &
  (AnchorProps | CatalystButtonProps);

export const AvatarButton = forwardRef<Ref<HTMLElement>, AvatarButtonProps>(
  function AvatarButton(
    {src, square = false, initials, alt, className, ...props},
    ref,
  ) {
    let classes = clsx(
      className,
      square ? 'rounded-[20%]' : 'rounded-full',
      'relative inline-grid focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500',
    );

    if ('href' in props) {
      const anchorProps = props as AnchorProps;

      return (
        <Link
          {...anchorProps}
          className={classes}
          ref={ref as Ref<HTMLAnchorElement>}>
          <TouchTarget>
            <Avatar src={src} square={square} initials={initials} alt={alt} />
          </TouchTarget>
        </Link>
      );
    }

    return (
      <Headless.Button
        {...props}
        className={classes}
        ref={ref as Ref<HTMLButtonElement>}>
        <TouchTarget>
          <Avatar src={src} square={square} initials={initials} alt={alt} />
        </TouchTarget>
      </Headless.Button>
    );
  },
);
